// NEW_FILE_JUSTIFIED: Column configuration arrays for all 10 insurance tables — no existing lib file defines IColumnConfig arrays; convex/tableColumnVisibility.ts is a backend module and has no column definitions
import type { IColumnConfig } from '@/@types/tableColumnVisibility';

// ============================================================================
// Insurance Table Column Configurations
// ============================================================================

export const CUSTOMERS_COLUMNS: IColumnConfig[] = [
  {
    id: 'name',
    label: 'Name',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    width: 'w-48',
    sortType: 'string',
  },
  {
    id: 'email',
    label: 'Email',
    group: 'Contact',
    defaultVisible: true,
    width: 'w-52',
    sortType: 'string',
  },
  {
    id: 'phone',
    label: 'Phone',
    group: 'Contact',
    defaultVisible: true,
    width: 'w-36',
    sortType: 'string',
  },
  {
    id: 'policies',
    label: 'Policies',
    group: 'Overview',
    defaultVisible: true,
    width: 'w-20',
    sortType: 'number',
  },
  {
    id: 'source',
    label: 'Source',
    group: 'Overview',
    defaultVisible: true,
    width: 'w-28',
    sortType: 'string',
  },
  {
    id: 'created',
    label: 'Created',
    group: 'Dates',
    defaultVisible: true,
    width: 'flex-1',
    sortType: 'date',
  },
  {
    id: 'actions',
    label: 'Actions',
    group: 'Actions',
    pinned: 'right',
    defaultVisible: true,
    width: 'w-20',
  },
];

export const POLICIES_COLUMNS: IColumnConfig[] = [
  {
    id: 'policyNumber',
    label: 'Policy #',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    width: 'w-[200px]',
    sortType: 'string',
  },
  {
    id: 'customer',
    label: 'Customer',
    group: 'Core',
    defaultVisible: true,
    width: 'w-[160px]',
    sortType: 'string',
  },
  {
    id: 'provider',
    label: 'Provider',
    group: 'Core',
    defaultVisible: true,
    width: 'w-[150px]',
    sortType: 'string',
  },
  {
    id: 'type',
    label: 'Type',
    group: 'Details',
    defaultVisible: true,
    width: 'w-[130px]',
    sortType: 'string',
  },
  {
    id: 'status',
    label: 'Status',
    group: 'Status',
    defaultVisible: true,
    width: 'w-[140px]',
    sortType: 'custom',
    customSortOrder: ['draft', 'under_review', 'rejected', 'active', 'expired', 'cancelled'],
  },
  {
    id: 'premium',
    label: 'Premium, \u20ac',
    group: 'Financial',
    defaultVisible: true,
    width: 'w-[110px]',
    sortType: 'number',
  },
  {
    id: 'invoice',
    label: 'Invoice',
    group: 'Financial',
    defaultVisible: true,
    width: 'w-[140px]',
    sortType: 'custom',
    customSortOrder: ['not_paid', 'partially_paid', 'paid', 'unknown'],
  },
  {
    id: 'createdBy',
    label: 'Created By',
    group: 'Details',
    defaultVisible: true,
    width: 'w-[140px]',
    sortType: 'string',
  },
  {
    id: 'issued',
    label: 'Issued',
    group: 'Dates',
    defaultVisible: true,
    width: 'w-[110px]',
    sortType: 'date',
  },
  {
    id: 'expires',
    label: 'Expires',
    group: 'Dates',
    defaultVisible: true,
    width: 'w-[110px]',
    sortType: 'date',
  },
];

export const RENEWAL_COLUMNS: IColumnConfig[] = [
  {
    id: 'policyNumber',
    label: 'Policy #',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    width: 'w-[200px]',
    sortType: 'string',
  },
  {
    id: 'customer',
    label: 'Customer',
    group: 'Core',
    defaultVisible: true,
    width: 'w-[160px]',
    sortType: 'string',
  },
  {
    id: 'provider',
    label: 'Provider',
    group: 'Core',
    defaultVisible: true,
    width: 'w-[150px]',
    sortType: 'string',
  },
  {
    id: 'type',
    label: 'Type',
    group: 'Details',
    defaultVisible: true,
    width: 'w-[130px]',
    sortType: 'string',
  },
  {
    id: 'status',
    label: 'Status',
    group: 'Status',
    defaultVisible: true,
    width: 'w-[140px]',
    sortType: 'custom',
    customSortOrder: ['draft', 'under_review', 'rejected', 'active', 'expired', 'cancelled'],
  },
  {
    id: 'premium',
    label: 'Premium, \u20ac',
    group: 'Financial',
    defaultVisible: true,
    width: 'w-[110px]',
    sortType: 'number',
  },
  {
    id: 'invoice',
    label: 'Invoice',
    group: 'Financial',
    defaultVisible: true,
    width: 'w-[140px]',
    sortType: 'custom',
    customSortOrder: ['not_paid', 'partially_paid', 'paid', 'unknown'],
  },
  {
    id: 'renewal',
    label: 'Renewal',
    group: 'Status',
    defaultVisible: true,
    width: 'w-[130px]',
    sortType: 'custom',
    customSortOrder: ['unknown', 'no_need', 'in_progress', 'completed'],
  },
  {
    id: 'notificationStatus',
    label: 'Notification',
    group: 'Status',
    defaultVisible: true,
    width: 'w-[130px]',
    sortType: 'custom',
    customSortOrder: ['pending', 'sent', 'error'],
  },
  {
    id: 'createdBy',
    label: 'Created By',
    group: 'Details',
    defaultVisible: true,
    width: 'w-[140px]',
    sortType: 'string',
  },
  {
    id: 'issued',
    label: 'Issued',
    group: 'Dates',
    defaultVisible: true,
    width: 'w-[110px]',
    sortType: 'date',
  },
  {
    id: 'expires',
    label: 'Expires',
    group: 'Dates',
    defaultVisible: true,
    width: 'w-[110px]',
    sortType: 'date',
  },
];

export const CLAIMS_COLUMNS: IColumnConfig[] = [
  {
    id: 'claimNumber',
    label: 'Claim #',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    sortType: 'string',
  },
  { id: 'claimant', label: 'Claimant', group: 'Core', defaultVisible: true, sortType: 'string' },
  {
    id: 'treatmentType',
    label: 'Treatment Type',
    group: 'Details',
    defaultVisible: true,
    sortType: 'string',
  },
  { id: 'amount', label: 'Amount', group: 'Financial', defaultVisible: true, sortType: 'number' },
  {
    id: 'status',
    label: 'Status',
    group: 'Status',
    defaultVisible: true,
    sortType: 'custom',
    customSortOrder: [
      'draft',
      'submitted',
      'under_review',
      'additional_info_required',
      'approved',
      'partially_approved',
      'rejected',
      'paid',
      'partially_paid',
    ],
  },
  {
    id: 'dateOfService',
    label: 'Date of Service',
    group: 'Dates',
    defaultVisible: true,
    sortType: 'date',
  },
  { id: 'provider', label: 'Provider', group: 'Details', defaultVisible: true, sortType: 'string' },
  { id: 'created', label: 'Created', group: 'Dates', defaultVisible: true, sortType: 'date' },
];

export const BENEFIT_PLANS_COLUMNS: IColumnConfig[] = [
  {
    id: 'name',
    label: 'Name',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    sortType: 'string',
  },
  { id: 'provider', label: 'Provider', group: 'Details', defaultVisible: true, sortType: 'string' },
  { id: 'type', label: 'Type', group: 'Details', defaultVisible: true, sortType: 'string' },
  { id: 'status', label: 'Status', group: 'Status', defaultVisible: true, sortType: 'string' },
  {
    id: 'maxAnnualBenefit',
    label: 'Max Annual Benefit',
    group: 'Financial',
    defaultVisible: true,
    sortType: 'number',
  },
  { id: 'created', label: 'Created', group: 'Dates', defaultVisible: true, sortType: 'date' },
];

export const COMPANIES_COLUMNS: IColumnConfig[] = [
  {
    id: 'name',
    label: 'Name',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    width: 'w-48',
    sortType: 'string',
  },
  {
    id: 'email',
    label: 'Email',
    group: 'Contact',
    defaultVisible: true,
    width: 'w-52',
    sortType: 'string',
  },
  {
    id: 'employees',
    label: 'Employees',
    group: 'Details',
    defaultVisible: true,
    width: 'w-24',
    sortType: 'number',
  },
  {
    id: 'members',
    label: 'Members',
    group: 'Details',
    defaultVisible: true,
    width: 'w-24',
    sortType: 'number',
  },
  {
    id: 'status',
    label: 'Status',
    group: 'Status',
    defaultVisible: true,
    width: 'w-28',
    sortType: 'custom',
    customSortOrder: ['pending', 'active', 'archived'],
  },
  {
    id: 'created',
    label: 'Created',
    group: 'Dates',
    defaultVisible: true,
    width: 'flex-1',
    sortType: 'date',
  },
  {
    id: 'actions',
    label: 'Actions',
    group: 'Actions',
    pinned: 'right',
    defaultVisible: true,
    width: 'w-20',
  },
];

export const INVOICES_COLUMNS: IColumnConfig[] = [
  {
    id: 'invoiceNumber',
    label: 'Invoice #',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    width: 'w-[160px]',
    sortType: 'string',
  },
  {
    id: 'company',
    label: 'Company',
    group: 'Core',
    defaultVisible: true,
    width: 'w-[180px]',
    sortType: 'string',
  },
  {
    id: 'total',
    label: 'Total',
    group: 'Financial',
    defaultVisible: true,
    width: 'w-[120px]',
    sortType: 'number',
  },
  {
    id: 'status',
    label: 'Status',
    group: 'Status',
    defaultVisible: true,
    width: 'w-[140px]',
    sortType: 'custom',
    customSortOrder: ['draft', 'sent', 'overdue', 'partially_paid', 'paid', 'cancelled'],
  },
  {
    id: 'dueDate',
    label: 'Due Date',
    group: 'Dates',
    defaultVisible: true,
    width: 'w-[120px]',
    sortType: 'date',
  },
  {
    id: 'created',
    label: 'Created',
    group: 'Dates',
    defaultVisible: true,
    width: 'flex-1',
    sortType: 'date',
  },
];

export const PREAUTH_COLUMNS: IColumnConfig[] = [
  {
    id: 'paNumber',
    label: 'PA Number',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'status',
    label: 'Status',
    group: 'Status',
    defaultVisible: true,
    sortType: 'custom',
    customSortOrder: ['draft', 'requested', 'under_review', 'approved', 'denied', 'expired'],
  },
  {
    id: 'treatmentType',
    label: 'Treatment Type',
    group: 'Details',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'proposedTreatment',
    label: 'Proposed Treatment',
    group: 'Details',
    defaultVisible: true,
    sortType: 'string',
  },
  { id: 'provider', label: 'Provider', group: 'Details', defaultVisible: true, sortType: 'string' },
  {
    id: 'estimatedCost',
    label: 'Estimated Cost',
    group: 'Financial',
    defaultVisible: true,
    sortType: 'number',
  },
  { id: 'submitted', label: 'Submitted', group: 'Dates', defaultVisible: true, sortType: 'date' },
];

export const PAYMENTS_COLUMNS: IColumnConfig[] = [
  {
    id: 'policyNumber',
    label: 'Policy #',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    sortType: 'string',
  },
  { id: 'customer', label: 'Customer', group: 'Core', defaultVisible: true, sortType: 'string' },
  { id: 'type', label: 'Type', group: 'Details', defaultVisible: true, sortType: 'string' },
  { id: 'issued', label: 'Issued', group: 'Dates', defaultVisible: true, sortType: 'date' },
  {
    id: 'invoiceStatus',
    label: 'Invoice Status',
    group: 'Status',
    defaultVisible: true,
    sortType: 'custom',
    customSortOrder: ['not_paid', 'partially_paid', 'paid', 'unknown'],
  },
  {
    id: 'partialAmount',
    label: 'Partial Amount',
    group: 'Financial',
    defaultVisible: true,
    sortType: 'number',
  },
  { id: 'snooze', label: 'Snooze', group: 'Actions', defaultVisible: true },
  { id: 'notifications', label: 'Notifications', group: 'Actions', defaultVisible: true },
  { id: 'actions', label: 'Actions', group: 'Actions', pinned: 'right', defaultVisible: true },
];

export const DRAFTS_COLUMNS: IColumnConfig[] = [
  {
    id: 'application',
    label: 'Application',
    group: 'Core',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'insuranceType',
    label: 'Insurance Type',
    group: 'Details',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'status',
    label: 'Status',
    group: 'Status',
    defaultVisible: true,
    sortType: 'custom',
    customSortOrder: ['draft', 'under_review', 'rejected'],
  },
  {
    id: 'createdBy',
    label: 'Created By',
    group: 'Details',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'lastUpdated',
    label: 'Last Updated',
    group: 'Dates',
    defaultVisible: true,
    sortType: 'date',
  },
];

export const PROVIDERS_COLUMNS: IColumnConfig[] = [
  {
    id: 'name',
    label: 'Name',
    group: 'Core',
    pinned: 'left',
    defaultVisible: true,
    sortType: 'string',
  },
  {
    id: 'isActive',
    label: 'Active',
    group: 'Status',
    defaultVisible: true,
    width: 'w-20',
  },
  { id: 'website', label: 'Website', group: 'Contact', defaultVisible: true, sortType: 'string' },
  { id: 'contacts', label: 'Contacts', group: 'Details', defaultVisible: true, sortType: 'number' },
  { id: 'portal', label: 'Portal', group: 'Details', defaultVisible: true, width: 'w-24' },
  {
    id: 'lastScraped',
    label: 'Last Scraped',
    group: 'Details',
    defaultVisible: true,
    width: 'w-36',
  },
];
